import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { server } from "../../server";
import Top from "./Top";
import Right from "./Right";
import { Link } from "react-router-dom";
import GLightbox from "glightbox";

function MyPhotos() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [stream, setStream] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const token = localStorage.getItem("authToken");
  const [searchResults, setSearchResults] = useState([]);
  const cameraErrorShown = useRef(false);

  // ✅ Start Camera
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
      });
      setStream(mediaStream);

      // slight delay to ensure DOM is ready
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.play().catch(() => {}); // avoid autoplay errors
        }
      }, 300);

      setCameraActive(true);
    } catch (err) {
      if (!cameraErrorShown.current) {
        toast.error("Camera Access Denied");
        cameraErrorShown.current = true;
      }
    }
  };

  // ✅ Stop Camera
  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
      setCameraActive(false);
    }
  };

  // ✅ Capture + Upload Selfie
  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || video.readyState < 2) {
      toast.info("Please Wait, Starting Camera", { autoClose: 2000 });
      return;
    }

    const ctx = canvas.getContext("2d");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const imageDataUrl = canvas.toDataURL("image/jpeg");
    setCapturedImage(imageDataUrl);

    // Stop the camera after capture
    stopCamera();

    canvas.toBlob(async (blob) => {
      const file = new File([blob], "selfie.jpg", { type: "image/jpeg" });

      const toastId = toast.loading("Uploading Selfie, Please Wait");
      try {
        await uploadAndSearch(file);
        toast.update(toastId, {
          render: "Selfie Uploaded",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
      } catch (err) {
        toast.update(toastId, {
          render: "Something Went Wrong",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    }, "image/jpeg");
  };

  // ✅ Upload + Search API
  const uploadAndSearch = async (file) => {
    try {
      const fileName = `selfie-${Date.now()}.jpg`;
      const presignedRes = await fetch(
        `${server}/PresignedUrl/GeneratePresignedUrl`,
        {
          method: "POST",
          headers: {
            Authorization: token,
          },
          body: JSON.stringify({
            folder: "search",
            fileNames: [fileName],
          }),
        }
      );

      const presignedData = await presignedRes.json();
      const uploadDetails = presignedData.urls[0];
      const s3Key = uploadDetails.imageKey;

      await fetch(uploadDetails.signedUrl, {
        method: "PUT",
        headers: {
          "Content-Type": uploadDetails.contentType,
        },
        body: file,
      });

      const searchRes = await fetch(
        `${server}/Search/GlobalSearch?key=${encodeURIComponent(s3Key)}`,
        {
          method: "GET",
        }
      );

      const searchData = await searchRes.json();

      if (searchData.images && searchData.images.length > 0) {
        setSearchResults(searchData.images);
      } else {
        toast.info("No Matches Found");
      }
    } catch (err) {
      toast.error("Something Went Wrong");
    }
  };

  useEffect(() => {
    const lightbox = GLightbox({
      selector: ".glightbox",
    });

    return () => lightbox.destroy();
  }, [searchResults]);

  return (
    <>
      <Top />

      <div className="container">
        <div className="row g-3">
          <div className="col-lg-9 col-12 py-2">
            <div className="mb-3 mt-2">
              <h4 className="text-white lh-1">Find Your Photos Instantly</h4>
              <p className="text-white opacity-75 mb-4 lh-1">
                Upload a selfie to instantly see every event photo you’re in—no
                tags, no search.
              </p>

              <div className="mt-5 d-flex">
                <div>
                  <div
                    className="rounded-circle overflow-hidden bg-light-primary d-flex align-items-center justify-content-center"
                    style={{ width: 150, height: 150 }}
                  >
                    {cameraActive ? (
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-100 h-100 object-fit-cover rounded-circle"
                        onCanPlay={() => videoRef.current?.play()}
                      />
                    ) : capturedImage ? (
                      <img
                        src={capturedImage}
                        width="150"
                        height="150"
                        className="object-fit-cover rounded-circle"
                        alt="Captured Selfie"
                      />
                    ) : (
                      <i className="bi bi-camera display-1"></i>
                    )}
                  </div>

                  <canvas ref={canvasRef} style={{ display: "none" }} />
                </div>

                <div className="ms-lg-5 ms-3 d-flex align-items-start flex-column justify-content-center">
                  {!cameraActive && !capturedImage ? (
                    <button
                      className="btn btn-dark-primary border-0 px-5 rounded-3 fs-6 fw-medium"
                      onClick={startCamera}
                    >
                      <i className="bi bi-camera me-1"></i> Capture Selfie
                    </button>
                  ) : (
                    <button
                      className="btn btn-dark-primary border-0 px-5 rounded-3 fs-6 fw-medium"
                      onClick={capturePhoto}
                      disabled={!stream}
                    >
                      <i className="bi bi-upload me-1"></i> Upload Selfie
                    </button>
                  )}

                  <p className="mt-lg-2 mt-3 text-white opacity-75 fs-7 lh-sm">
                    Upload a clear photo — avoid blurry images.
                  </p>
                </div>
              </div>

              {searchResults.length > 0 && (
                <>
                  <h5 className="text-light my-4">
                    Search Results ({searchResults.length}) Images
                  </h5>
                  <div className="row g-2 mb-lg-4">
                    {searchResults.map((item, index) => (
                      <div className="col-lg-2 col-md-3 col-4" key={index}>
                        <a href={item.imageUrl} className="glightbox">
                          <img
                            src={item.imageUrl}
                            alt={`match-${index}`}
                            className="w-100 object-fit-cover rounded-4"
                            height="110"
                            loading="lazy"
                          />
                        </a>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="col-lg-3 col-12">
            <Right />
          </div>
        </div>
      </div>
    </>
  );
}

export default MyPhotos;
